<template lang="html">
  <div class="container" style="height:74vh;">
    <h2 style="text-align: center;">CitSci Account Email Verification</h2>

    <div class="row card-body justify-content-center">

      <!-- 1. No token in URL -->
      <div v-if="is1NoToken || test" class="mt-3">
        <div class="row">
          <div class="flex-none">
            <i class="fa fa-exclamation-triangle fa-3x mr-3 text-warning"></i>
          </div>
          <div class="font-weight-bold flex-none">
            No verification token found.
            Please click the link in the verification email that was sent to you.
          </div>
        </div>
      </div>

      <!-- 2. Invalid token -->
      <div v-else-if="is2TokenBad || test" class="mt-3">
        <div class="row">
          <div class="flex-none">
            <i class="fa fa-close fa-3x mr-3 text-danger"></i>
          </div>
          <div class="font-weight-bold flex-none">
            The verification link is invalid. Please go back to the email
            and click the link again.<br>This was most likely caused by not copying
            the full URL.
          </div>
        </div>
      </div>

      <!-- 3. Token expired -->
      <div class="mt-3" v-else-if="is3TokenExpired || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-exclamation-triangle fa-3x mr-3 text-warning"></i>
            </div>
            <div class="font-weight-bold flex-none">
              Your CitSci account email verification link expired {{ tokenExpiryDateTime }}.
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 py-3 font-weight-bold">
            Click the button below to receive a new verification email.
          </div>
        </div>
        <div class="row">
          <div class="col-12 py-3">
            <button class="btn btn-primary mt-2"
                    :disabled="isSendingEmail"
                    @click="onClickResendEmail">
              <i v-if="isSendingEmail" class="fa fa-spin fa-spinner mr-1"></i>
              Resend Verification Email
            </button>
          </div>
        </div>
        <i class="text-muted">Note: CitSci account email verification links expire after 30 days.</i>
      </div>

      <!-- 4. Wrong user (logged in but token belongs to a different account) -->
      <div class="mt-3" v-else-if="is4WrongUser || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-exclamation-triangle fa-3x mr-3 text-warning"></i>
            </div>
            <div class="font-weight-bold flex-none">
              This verification link belongs to a different account.
              You are currently logged in as "{{ userEmail }}".
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 mt-3 ml-4 font-weight-bold">
            Log out and then click the CitSci account email verification link again.
          </div>
        </div>
        <div class="row">
          <div class="col-4 ml-4">
            <router-link :to="{ name: 'Logout', }" class="btn btn-primary btn-block mt-2" replace>
              Logout
            </router-link>
          </div>
        </div>
      </div>

      <!-- 5. Already verified (logged in + verified + same user) -->
      <div class="mt-3" v-else-if="is5AlreadyVerified || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-check fa-3x mr-3 text-success"></i>
            </div>
            <div class="font-weight-bold flex-none">
              Your CitSci account email is <u>already</u> verified.
              You may add data or explore CitSci using the navigation above.
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 ml-4">
            <router-link :to="{ name: 'ProfileShow' }" class="btn btn-primary btn-block mt-2" replace>
              Go To My Profile
            </router-link>
          </div>
        </div>
      </div>

      <!-- 6. Activating -->
      <div class="mt-3" v-else-if="is6Activating || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-spin fa-spinner fa-3x mr-3"></i>
            </div>
            <div class="font-weight-bold flex-none">
              Verifying your email and activating your CitSci account...
            </div>
          </div>
        </div>
      </div>

      <!-- 7. Server error -->
      <div class="mt-3" v-else-if="is7ServerError || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-close fa-3x mr-3 text-danger"></i>
            </div>
            <div class="font-weight-bold flex-none">
              There was an error activating your account.
            </div>
          </div>
        </div>
        <div class="row ml-4">
          <div class="col-auto mt-3">
            <b>A. </b> Try again.
            <button class="btn btn-tertiary btn-block mt-2" @click="activateAccount">
              Try Again
            </button>
          </div>
          <div class="col-auto mt-3">
            <b>B. </b> Submit a help request.
            <ContactUsIconLink
              class="btn btn-secondary btn-block"
              hyperlink-text="Submit Help Request">
            </ContactUsIconLink>
          </div>
        </div>
        <div class="mt-3" v-if="activateServerError">{{ activateServerError }}</div>
      </div>

      <!-- 8. Activated successfully -->
      <div class="mt-3" v-else-if="is8Activated || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-check fa-3x mr-3 text-success"></i>
            </div>
            <div class="font-weight-bold flex-none">
              Your email has been verified and your CitSci account is now activated!<br>
              You may now create or join projects and contribute data.
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-auto mt-3 ml-4">
            <router-link :to="{ name: 'ProfileShow' }" class="btn btn-primary btn-block mt-2" replace>
              Go To My Profile
            </router-link>
          </div>
        </div>
      </div>

      <!-- 9. Email sent confirmation -->
      <div class="mt-3" v-else-if="is9EmailSent || test">
        <div class="row">
          <div class="col-12 d-flex align-items-center">
            <div class="flex-none">
              <i class="fa fa-check fa-3x mr-3 text-success"></i>
            </div>
            <div class="font-weight-bold flex-none">
              Verification email sent successfully!
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-3 ml-4">
            Be sure to check the spam folder
            and add <strong>noreply@citsci.org</strong> to your safe sender's list.
            <br><br>
            <i class="text-muted">Note: Verification links expire after 30 days.</i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ContactUsIconLink from "../../layout/ContactUsIconLink.vue";

export default {
  components: {ContactUsIconLink},
  data() {
    return {
      is1NoToken: null,
      is4WrongUser: null,
      is5AlreadyVerified: null,
      is7ServerError: null,
      is8Activated: null,
      token: null,
      test: false, // set to true to show all scenarios at once for debugging
    }
  },

  created() {
    console.log('created')
    this.processActivation()
  },

  beforeDestroy() {
    this.reset()
  },

  computed: {
    ...mapGetters({
      hasSession: 'user/profile/hasSession',
      isVerified: 'user/profile/isVerified',
      userId: 'user/profile/id',
      userEmail: 'user/profile/email',

      is2TokenBad: 'user/activate/invalidToken',
      is3TokenExpired: 'user/activate/expiredToken',
      is6Activating: 'user/activate/isLoading',
      is9EmailSent: 'user/activate/emailSent',
      decodedToken: 'user/activate/decodedToken',
      tokenExpiryDateTime: 'user/activate/tokenExpiryDateTime',
      activateServerError: 'user/activate/error',
      isSendingEmail: 'user/activate/isLoadingEmail',
    }),
  },

  methods: {
    ...mapActions({
      activate: 'user/activate/activate',
      checkToken: 'user/activate/checkToken',
      reset: 'user/activate/reset',
      resendEmail: 'user/activate/resendEmail',
    }),

    processActivation() {
      // Parse token from URL
      this.token = this.$route.params.token ? decodeURIComponent(this.$route.params.token) : null

      // 1. No token in URL
      if (!this.token) {
        this.is1NoToken = true
        return
      } else {
        this.is1NoToken = false
      }

      // 2 & 3. Decode, validate structure, check expiry - all handled by the store
      this.checkToken(this.token)

      // If token is bad or expired, stop here (template will show is2TokenBad or is3TokenExpired)
      if (this.is2TokenBad || this.is3TokenExpired) {
        return
      }

      // --- Login-dependent checks ---
      // 4. User is logged in but token belongs to a different user
      if (this.hasSession && this.userId && this.decodedToken && this.decodedToken.code !== this.userId) {
        this.is4WrongUser = true
      } else {
        this.is4WrongUser = false
      }

      // 5. User is logged in and already verified (same user)
      if (this.hasSession && this.isVerified) {
        this.is5AlreadyVerified = true
      } else {
        this.is5AlreadyVerified = false
      }

      // All checks passed - activate
      if (
        !this.is1NoToken &&
        !this.is2TokenBad &&
        !this.is3TokenExpired &&
        !this.is4WrongUser &&
        !this.is5AlreadyVerified
      ) {
        this.activateAccount()
      }
    },

    activateAccount() {
      this.activate(this.token)
        .then(() => {
          if (!this.activateServerError) {
            this.is7ServerError = false
            this.is8Activated = true
          }
        })
        .catch(() => {
          this.is7ServerError = true
        })
    },

    onClickResendEmail() {
      if (!this.decodedToken || !this.decodedToken.code) return
      this.resendEmail({id: this.decodedToken.code, email: this.userEmail})
    },
  },

  watch: {
    activateServerError(error) {
      console.log('server error watch -> ', error)
      if (error) {
        this.is7ServerError = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
